// ============================================================================
// Ask AI's New Accounts card — what each action shows, and its save (item #944,
// the new-accounts part; TCSD-0531 one tool per part, saved through the
// screen's own function; TCSD-0533 a card that cannot be undone reads it back).
// The server half is supabase/functions/am-copilot/new-accounts.ts.
//
// Every save here is the function the card screen (sop-detail.tsx) calls for
// the same button: patchSopCard (nas_card_patch — the footer's Move to, Mark
// stalled, Cancel card, and the Notes box), recordSopSignoff, deleteSopCard,
// archiveSopCard. A stage move re-reads the card when Do it is pressed and asks
// the screen's own rule (lib/stage-move.ts). Adding to the notes re-reads the
// notes then, so words someone typed meanwhile are kept.
// ============================================================================

import {
  archiveSopCard,
  deleteSopCard,
  fetchSopCard,
  patchSopCard,
  recordSopSignoff,
  deleteBuildingWords,
  type SopDeletePreview,
  type SopPatch,
} from '../new-account-sop/hooks/use-sop-data';
import { stageMoveCheck } from '../new-account-sop/lib/stage-move';
import type { NewAccountSopStage } from '../../shared/types';
import { localToday } from '../../shared/format';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { nasStageWords, newAccountButtonWords } from '../../../supabase/functions/am-copilot/buttons';
import type { FieldSpec } from './building-actions';

export const NEW_ACCOUNT_ACTIONS = [
  'move_stage',
  'mark_stalled',
  'cancel_card',
  'add_note',
  'record_signoff',
  'delete_card',
  'archive_card',
  'restore_card',
] as const;
export type NewAccountAction = (typeof NEW_ACCOUNT_ACTIONS)[number];

/** A draft as the server persisted it (prepareNewAccountAction). */
export interface NewAccountDraft {
  sop_id: string;
  action: NewAccountAction;
  fields: Record<string, unknown>;
  facility_id?: string | null;
  display_name?: string | null;
  context?: Record<string, unknown>;
}

export interface NewAccountCardSpec {
  title: string;
  fields: FieldSpec[];
  info: Array<{ label: string; value: string }>;
  readBack: string | null;
  doItLabel: string | null;
  missing: string | null;
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const blank = (v: unknown) => str(v).trim().length === 0;
const ctxOf = (d: NewAccountDraft) => (d.context ?? {}) as Record<string, unknown>;

/** The card as the board names it. */
export const newAccountOf = (d: NewAccountDraft) => str(ctxOf(d).label) || str(d.display_name) || d.sop_id;

/** Notes after "add to the notes": the card's notes, then the new words on their own line. */
export function appendedNotes(current: string | null | undefined, text: string): string {
  const now = (current ?? '').replace(/\s+$/, '');
  return now ? `${now}\n${text.trim()}` : text.trim();
}

/** The confirm's sentence about the building the card made (deleteBuildingWords, the screen's own). */
const buildingLine = (b: unknown) => {
  const w = deleteBuildingWords((b ?? null) as SopDeletePreview | null);
  return w ? ` ${w}` : '';
};

/** What the card shows for this draft, with the person's edits `f`. */
export function newAccountCard(d: NewAccountDraft, f: Record<string, unknown>): NewAccountCardSpec {
  const c = ctxOf(d);
  const stageLine = { label: 'Stage now', value: str(c.stage_words) || '—' };
  const base: NewAccountCardSpec = {
    title: '',
    fields: [],
    info: [stageLine],
    readBack: null,
    doItLabel: newAccountButtonWords(d.action, f),
    missing: null,
  };
  switch (d.action) {
    case 'move_stage':
      return { ...base, title: `Move to ${nasStageWords(f.to)}` };
    case 'mark_stalled':
      return { ...base, title: 'Mark stalled' };
    case 'cancel_card':
      return {
        ...base,
        title: 'Cancel the card',
        fields: [{ key: 'reason', label: 'Why it is not going ahead (shown on the card)', kind: 'textarea', required: true }],
        readBack: `This cancels ${newAccountOf(d)} — it leaves the board as Cancelled, dated today.`,
        missing: blank(f.reason) ? 'Say why it is being cancelled.' : null,
      };
    case 'add_note':
      return {
        ...base,
        title: 'Add to the notes',
        info: [],
        fields: [{ key: 'text', label: 'Add to the notes', kind: 'textarea', required: true, now: blank(c.notes_now) ? 'No notes yet.' : str(c.notes_now) }],
        missing: blank(f.text) ? 'Write what to add.' : null,
      };
    case 'record_signoff': {
      const what = f.kind === 'exec_review' ? 'executive review' : 'confirmation call';
      return {
        ...base,
        title: f.kind === 'exec_review' ? 'Record the executive review' : 'Record the confirmation call',
        info: [stageLine, { label: 'Recorded as', value: `the ${what}, today, by you` }],
      };
    }
    case 'delete_card':
      return {
        ...base,
        title: 'Delete the card',
        fields: [{ key: 'reason', label: 'Why it was made in error', kind: 'textarea', required: true }],
        readBack: `This deletes ${newAccountOf(d)} from the board — for a card made in error, not one that is not going ahead (that is Cancel card).${buildingLine(c.building)} Ask AI cannot undo it.`,
        missing: blank(f.reason) ? 'A reason is required to delete a card.' : null,
      };
    case 'archive_card':
      return {
        ...base,
        title: 'Archive the card',
        readBack: `This closes ${newAccountOf(d)}: it leaves the board and its details stop changing. It can be restored from the Archived lens.`,
      };
    case 'restore_card':
      return { ...base, title: 'Restore to the board' };
  }
}

export interface NewAccountCommitResult {
  ok: boolean;
  error?: string;
  note?: string;
  target_table?: string;
  target_row_id?: string | null;
  committed_payload?: Record<string, unknown>;
}
const fail = (error: string): NewAccountCommitResult => ({ ok: false, error });

/** The database refused one or more fields: its own sentences, never a second copy of the rule. */
const refusedWords = (refused: Record<string, string>) => Object.values(refused).join(' ');

async function patch(sopId: string, p: SopPatch): Promise<string | null> {
  const refused = await patchSopCard(sopId, p);
  return Object.keys(refused).length ? refusedWords(refused) : null;
}

/** Save one new-account draft through the card screen's own function. */
export async function commitNewAccountAction(
  d: NewAccountDraft,
  f: Record<string, unknown>,
  eid: string | null,
): Promise<NewAccountCommitResult> {
  const missing = newAccountCard(d, f).missing;
  if (missing) return fail(missing);
  if (!eid) return fail('Your sign-in has no employee record, so nothing can be saved in your name.');
  const id = d.sop_id;
  const name = newAccountOf(d);
  const ok = (note: string, committed_payload: Record<string, unknown>): NewAccountCommitResult =>
    ({ ok: true, note, target_table: 'new_account_sop', target_row_id: id, committed_payload });
  try {
    switch (d.action) {
      case 'move_stage': {
        const to = str(f.to) as NewAccountSopStage;
        // The footer's own rule, on the card as it is now.
        const live = await fetchSopCard(id);
        if (!live) return fail('That card could not be read.');
        if (live.stage === to) return fail(`${name} is already at ${nasStageWords(to)}.`);
        const { blocked } = stageMoveCheck(live, to);
        if (blocked) return fail(blocked);
        const no = await patch(id, { stage: to });
        if (no) return fail(no);
        return ok(`${name} — moved to ${nasStageWords(to)}.`, { stage: to });
      }
      case 'mark_stalled': {
        const no = await patch(id, { status: 'Stalled' });
        if (no) return fail(no);
        return ok(`${name} — marked stalled.`, { status: 'Stalled' });
      }
      case 'cancel_card': {
        const p: SopPatch = { stage: 'Cancelled', status: 'Cancelled', cancelled_date: localToday(), cancelled_reason: str(f.reason).trim() || null };
        const no = await patch(id, p);
        if (no) return fail(no);
        return ok(`${name} — cancelled.`, p as Record<string, unknown>);
      }
      case 'add_note': {
        const live = await fetchSopCard(id);
        if (!live) return fail('That card could not be read.');
        const notes = appendedNotes(live.notes, str(f.text));
        const no = await patch(id, { notes });
        if (no) return fail(no);
        return ok('Added to the notes.', { added: str(f.text).trim() });
      }
      case 'record_signoff': {
        const kind = f.kind === 'exec_review' ? 'exec_review' : 'confirmation_call';
        await recordSopSignoff(id, kind);
        return ok(kind === 'exec_review' ? 'Executive review recorded.' : 'Confirmation call recorded.', { signoff: kind });
      }
      case 'delete_card':
        await deleteSopCard(id, str(f.reason).trim());
        return ok('Card deleted.', { reason: str(f.reason).trim() });
      case 'archive_card':
        await archiveSopCard(id, true);
        return ok(`${name} — archived.`, { archived: true });
      case 'restore_card':
        await archiveSopCard(id, false);
        return ok(`${name} — back on the board.`, { archived: false });
    }
  } catch (e) {
    return fail(rpcErrorMessage(e, 'That did not save.'));
  }
}
